import {MaybeRefOrGetter} from "@vueuse/core";
import {toValue} from "vue";

export default class ObjectChecker {

  static isNotEmptyOrNull<T>(o: T | null | undefined): o is T {
    return !this.isEmptyOrNull(o);
  }

  static isEmptyOrNull<T>(o: MaybeRefOrGetter<T | null | undefined>): o is (null | undefined) {
    const val = toValue(o);
    return this.isEmpty(val) || this.isNull(val);
  }

  static areAllNotEmptyOrNull(...list: unknown[]) {
    for (const el of list) {
      if (this.isEmptyOrNull(el))
        return false;
    }
    return true;
  }

  static areAllEmptyOrNull(...list: unknown[]) {
    for (const el of list) {
      if (this.isNotEmptyOrNull(el))
        return false;
    }
    return true;
  }

  static isEmpty(o: unknown): boolean {
    if (Array.isArray(o) || typeof o === "string")
      return o.length === 0
    else if (o && typeof o == "object")
      return Object.keys(o).length === 0
    else
      return false;
  }

  static isNotNull<T>(o: T | null | undefined): o is NonNullable<T> {
    return o !== null && o !== undefined;
  }

  static isNull(o: unknown): o is null | undefined {
    return !this.isNotNull(o);
  }
  static isAnyEmptyOrNull(...args: any[]) {
    for (let arg of args) {
      if (this.isEmptyOrNull(arg))
        return true;
    }
    return false;
  }

  static isAnyNotEmptyOrNull(...args: any[]) {
    for (let arg of args) {
      if (this.isNotEmptyOrNull(arg))
        return true;
    }
    return false;
  }

  public static isString(o: unknown): o is string {
    return typeof o === "string";
  }

  static isNumber(o: unknown): o is number {
    return typeof o === "number" && !isNaN(o);
  }

  static isObject(o: unknown): o is object {
    return o !== null && typeof o === "object" && !this.isArray(o);
  }

  static isArray(o: unknown): o is any[] {
    return Array.isArray(o);
  }

  static isEmptyOrNullish<T>(o: T | null | undefined): o is null | undefined {
    if (o == null)
      return true;
    else if (this.isString(o))
      return this.isEmptyString(o);
    else if (this.isArray(o))
      return this.isEmptyArray(o);
    else if (this.isObject(o))
      return this.isEmptyObject(o);
    return false;
  }

  static isNotEmptyOrNullish<T>(o: T | null | undefined): o is NonNullable<T> {
    return !this.isEmptyOrNullish(o);
  }

  private static isEmptyString(o: string) {
    return o.trim().length < 1;
  }

  static isEmptyObject(o: object) {
    return Object.keys(o).length === 0;
  }

  private static isEmptyArray(o: any[]) {
    return o.length === 0;
  }

  static isLastElement(element: any, array: any[]) {
    return array[array.length - 1] === element;
  }

  static isNotNullOrUndefined(o: unknown) {
    return !this.isNullOrUndefined(o);
  }

  static isNullOrUndefined(o: unknown) {
    return o == null;
  }

  static areEqual(o1: any, o2: any) {
    if (o1 === o2)
      return true;
    if (o1 && o2) {
      if (o1.id || o2.id)
        return o1.id === o2.id;
      if (o1.empty && o2.empty)
        return true;
    }
    return JSON.stringify(o1) === JSON.stringify(o2);
  }

  static areNotEqual(o1: any, o2: any) {
    return !this.areEqual(o1, o2);
  }

  static areAllEqual(...array: any[]): boolean {
    if (array.length <= 1)
      return true;
    else
      return array.every(el => this.areEqual(el, array[0]))
  }

  static getFirstNotEmptyObj(o1: any, o2: any) {
    if (ObjectChecker.isNotEmptyOrNull(o1))
      return o1;
    else
      return o2;
  }

  static isNotString(o: any) {
    return !this.isString(o);
  }

  static isBoolean(val: unknown) :val is boolean{
    return typeof val === "boolean";
  }
}